/*
 * ApplicationQaReportsPopUp.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Collapse,
  Modal,
} from "@mui/material";
import {
  Sms,
  ArrowDropUpRounded,
  ArrowDropDownRounded,
} from "@mui/icons-material";
import Moment from "moment";
import APIManager from "../../Services/ApiManager/ApiManager";
import PopupDialog from "../../Components/PopupDialog";
import { useParams } from "react-router-dom";
import { FileUploader } from "react-drag-drop-files";
import { fetchSubmissionDetails } from "../../Services/redux/Application-details/ApplicationDetailsActions";
import { useDispatch } from "react-redux";
import "./ApplicationQaReportsPopUp.css";
export default function ApplicationQaReportsPopUp({
  qaReports = [],
  openAddReportPopup,
  setOpenAddReportPopup,
}) {
  const dispatch = useDispatch();
  const [reportComment, setReportComment] = useState("");
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [expandedReports, setExpandedReports] = useState({});
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const { appId, submissionId } = useParams();
  const fileTypes = ["pdf", "txt", "zip"];
  // Helper function to decode URL-encoded comments
  const decodeComment = (comment) => {
    if (!comment) return comment;
    try {
      return decodeURIComponent(comment);
    } catch {
      return comment
        .replaceAll('%20', " ")
        .replaceAll(/%([0-9A-Fa-f]{2})/g, (match, p1) => {
          try {
            return String.fromCodePoint(Number.parseInt(p1, 16));
          } catch {
            return match;
          }
        });
    }
  };

  const handleDownload = (url, fileName) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "blob";
    xhr.withCredentials = false;

    xhr.onload = function () {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const fileExtension = fileName?.split(".").pop().toLowerCase();
        const mimeType = blob.type || "application/octet-stream";

        const fileBlob = new Blob([blob], { type: mimeType });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(fileBlob);
        link.download = fileName || `file.${fileExtension || "bin"}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
      } else {
        console.error("Error downloading the file:", xhr.statusText);
        setComponentName("downloadError");
        setErrorMessage("Failed to download the file. Please try again.");
        setHeaderMessage("Download Error");
        setOpenErrorPopup(true);
      }
    };

    xhr.onerror = function () {
      console.error("Network error downloading the file");
      setComponentName("downloadError");
      setErrorMessage(
        "Network error occurred while downloading. Please check your connection."
      );
      setHeaderMessage("Download Error");
      setOpenErrorPopup(true);
    };

    xhr.send();
  };

  const handleReportToggle = (reportId) => {
    setExpandedReports((prev) => ({
      ...prev,
      [reportId]: !prev[reportId],
    }));
  };

  const handleFileChange = (file) => {
    setFileName(file.name);
    setFileData(file);
  };

  const handleReportComment = ($event) => {
    setReportComment($event.target.value);
  };
  const deleteFile = () => {
    setFileData(null);
    setFileName("");
  };
  const handleAddMoreReports = async () => {
    setLoader(true);
    try {
      await APIManager.postAddMoreReports(
        fileData,
        appId,
        submissionId,
        reportComment
      );
      setOpenAddReportPopup(false);
      setFileData(null);
      setFileName("");
      setReportComment("");
      dispatch(fetchSubmissionDetails(appId, submissionId));
    } catch (error) {
      console.error("Error fetching data:", error);
      setComponentName("appError");
      setErrorMessage(error.message);
      setHeaderMessage(
        error.message === "jwt expired" ? "401 Token Expired" : "Error"
      );
      setOpenErrorPopup(true);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div>
      <div
        style={{
          marginLeft: "2%",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "2%",
        }}
      >
       
      </div>
      {qaReports.map((reports, idx) => (
        <Box key={reports.id || reports.fileName || reports.createdAt} sx={{ px: 2, marginTop: "10px" }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: expandedReports[idx] ? "grey.100" : {},
              p: "10px",
              mb: "8px",
              border: "1px solid #BDBDBD",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: expandedReports[idx] ? 1 : 0,
              }}
            >
              <Box sx={{ width: "25%" }}>
                <Typography
                  style={expandedReports[idx] ? { fontWeight: "bold" } : {}}
                >
                  {reports?.extLink ? (
                    <a
                      href={reports.extLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0476E2",
                        textDecoration: "none",
                        fontWeight: expandedReports[idx] ? "bold" : "normal",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.textDecoration = "underline")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.textDecoration = "none")
                      }
                    >
                      {reports?.extLink}
                    </a>
                  ) : (
                    reports?.fileName
                  )}
                </Typography>
              </Box>
              <Box className="domain-section">
                <Box
                  style={
                    expandedReports[idx]
                      ? { backgroundColor: "rgba(232, 232, 232, 1)" }
                      : {}
                  }
                  sx={{
                    backgroundColor: "rgba(232, 232, 232, 1)",
                    borderRadius: "4px",
                    maxWidth: "max-content",
                    height: "max-content",
                    fontSize: "12px",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  {reports.uploadedBy ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                        padding: "5px 8px 5px 8px",
                        fontWeight: "bold",
                        color: "#2A2C2D",
                      }}
                    >
                      {reports.uploadedBy === "Oem Admin"
                        ? "OEM admin"
                        : reports.uploadedBy}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                        padding: "5px 8px 5px 8px",
                        fontWeight: "bold",
                        color: "#2A2C2D",
                      }}
                    >
                      "N/A"
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2">
                  {Moment(reports?.createdAt).format("DD MMM YYYY")}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: "25%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {reports.fileName && !reports.extLink && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleDownload(reports.url, reports.fileName)
                      }
                      sx={{ mr: 1 }}
                      aria-label="Download report"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 12L3 7L4.4 5.55L7 8.15V0H9V8.15L11.6 5.55L13 7L8 12ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
                          fill="#121415"
                          fillOpacity="0.6"
                        />
                      </svg>
                    </IconButton>
                  )}
                  <IconButton size="small">
                    <Sms fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => handleReportToggle(idx)}
                  >
                    {expandedReports[idx] ? (
                      <ArrowDropUpRounded />
                    ) : (
                      <ArrowDropDownRounded />
                    )}
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            <Collapse in={expandedReports[idx]}>
              <Box>
                <Box
                  className="domain-section"
                  sx={{ backgroundColor: "rgba(232, 232, 232, 1)" }}
                >
                  {" "}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  mb={0.5}
                  color="#2A2C2D"
                >
                  Comment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {decodeComment(reports?.comment)}
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        </Box>
      ))}

      {/* Add More Reports Popup */}
      <Modal
        open={openAddReportPopup}
        onClose={() => {
          setOpenAddReportPopup(false);
          setFileData(null);
          setFileName("");
          setReportComment("");
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            p: 0,
            minWidth: "600px",
            maxWidth: "700px",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              pb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: "20px",
                color: "#2A2C2D",
              }}
            >
              Submit Report
            </Typography>
            <IconButton
              onClick={() => {
                setOpenAddReportPopup(false);
                setFileData(null);
                setFileName("");
                setReportComment("");
              }}
              sx={{
                p: 0,
                color: "#666",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, pb: 3 }}>
            {/* Upload Report Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: "600",
                  fontSize: "16px",
                  mb: 1,
                  color: "#2A2C2D",
                }}
              >
                Upload Report <span style={{ color: "red" }}>*</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  mb: 2,
                  color: "#5A5B5C",
                }}
              >
                Report must be pdf, txt, or zip file.
              </Typography>

              {/* File Upload Area - using your existing pattern */}
              <Stack direction="row" alignItems="center" spacing={2}>
                  {fileData === null ? (
                  <FileUploader
                    classes="fileContentClsUploadCreateNewRelease hide-file-types"
                    multiple={false}
                    handleChange={handleFileChange}
                    name="file"
                    types={fileTypes}
                    uploadedLabel="Uploaded Successfully!"
                    hoverTitle=""
                    dropMessageStyle={{ display: "none" }}
                  >
                    <div
                      style={{
                        padding: "24px",
                        textAlign: "center",
                        color: "#121415",
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ color: "#0476E2" }}>Browse&nbsp;</span> or
                      Drag & drop the report file here
                    </div>
                  </FileUploader>
                ) : (
                  <div
                    className="fileName-popup-div"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "12px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <div
                      className="filename-popup-text"
                      style={{
                        fontSize: "14px",
                        color: "#2A2C2D",
                        fontWeight: "500",
                      }}
                    >
                      {fileName}
                    </div>
                    <button
                      onClick={deleteFile}
                      aria-label="Remove file"
                      style={{ cursor: "pointer", background: "none", border: "none", padding: 0, display: "flex" }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20"
                          stroke="#666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </Stack>
            </Box>

            {/* Comment Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: "600",
                  fontSize: "16px",
                  mb: 1,
                  color: "#2A2C2D",
                }}
              >
                Comment
              </Typography>
              <textarea
                placeholder="Enter your comment here"
                value={reportComment}
                onChange={handleReportComment}
                style={{
                  width: "100%",
                  minHeight: "80px",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button
                onClick={() => {
                  setOpenAddReportPopup(false);
                  setFileData(null);
                  setFileName("");
                  setReportComment("");
                }}
                style={{
                  backgroundColor: "#1214151A",
                  color: "#2A2C2D",
                  border: "none",
                  borderRadius: "4px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  cursor: "pointer",
                  fontWeight: "500",
                  width: "114px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMoreReports}
                disabled={loader || !fileData}
                style={{
                  backgroundColor: loader || !fileData ? "#ccc" : "#0476E2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  cursor: loader || !fileData ? "not-allowed" : "pointer",
                  opacity: loader || !fileData ? 0.6 : 1,
                  fontWeight: "500",
                  width: "114px",
                }}
              >
                {loader ? "Submitting..." : "Submit"}
              </button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
    </div>
  );
}

ApplicationQaReportsPopUp.propTypes = {
  qaReports: PropTypes.array,
  openAddReportPopup: PropTypes.bool.isRequired,
  setOpenAddReportPopup: PropTypes.func.isRequired,
};
